import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["formId", "portalId", "env", "region", "editorVersion", "resolveEditorVersion", "loadingFallback", "prefill"],
  _excluded2 = ["onFormEvent"];
import { useCallback, useRef, useState } from 'react';
import styled from 'foundations-theming-specialty/utils';
import { useResolveEditorVersion } from './useResolveEditorVersion';
import { useFormEventDispatch } from './useFormEventDispatch';
import { VersionOneForm } from './VersionOneForm';
import { VersionTwoForm } from './VersionTwoForm';
import { FormLoadingPlaceholder } from './FormLoadingPlaceholder';
import { getPortalHublet } from '../../utils/metadata';
import { applyPrefill } from './prefill';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "HubSpotForm__Wrapper"
})(["transition:opacity 0.2s ease-in-out;opacity:", ";"], ({
  $visible
}) => $visible ? 1 : 0);
export function HubSpotForm(_ref) {
  var _ref2;
  let {
      formId,
      portalId,
      env,
      region: regionProp,
      editorVersion: editorVersionProp,
      resolveEditorVersion,
      loadingFallback,
      prefill
    } = _ref,
    eventCallbacks = _objectWithoutPropertiesLoose(_ref, _excluded);
  const region = (_ref2 = regionProp !== null && regionProp !== void 0 ? regionProp : getPortalHublet()) !== null && _ref2 !== void 0 ? _ref2 : '';
  const [isReady, setIsReady] = useState(false);
  const resolvedVersion = useResolveEditorVersion({
    formId,
    portalId,
    env,
    editorVersion: editorVersionProp,
    resolveEditorVersion
  });
  const prefillAppliedRef = useRef(false);
  const prevFormKeyRef = useRef(`${portalId}:${formId}:${env}`);
  const formKey = `${portalId}:${formId}:${env}`;
  if (prevFormKeyRef.current !== formKey) {
    prevFormKeyRef.current = formKey;
    prefillAppliedRef.current = false;
    setIsReady(false);
  }
  const {
      onFormEvent
    } = eventCallbacks,
    restEventCallbacks = _objectWithoutPropertiesLoose(eventCallbacks, _excluded2);

  // Track readiness from any dispatched onFormReady — even ones without
  // an instance (e.g. V2's data-loaded observer signal). The user-facing
  // onFormReady callback still only fires when an instance is available.
  // Prefill is applied on the instance-bearing onFormReady (before the
  // consumer's callback) so values are set on a fully rendered form.
  const handleFormEvent = useCallback((eventName, instance) => {
    if (eventName === 'onFormReady') {
      setIsReady(true);
      if (instance && prefill && !prefillAppliedRef.current) {
        prefillAppliedRef.current = true;
        applyPrefill(instance, prefill).catch(() => {
          // Prefill is best-effort; a failure should not break the form
        });
      }
    }
    onFormEvent === null || onFormEvent === void 0 || onFormEvent(eventName, instance);
  }, [onFormEvent, prefill]);
  const dispatch = useFormEventDispatch(Object.assign({}, restEventCallbacks, {
    onFormEvent: handleFormEvent
  }));
  const fallback = loadingFallback === undefined ? /*#__PURE__*/_jsx(FormLoadingPlaceholder, {}) : loadingFallback;
  if (resolvedVersion == null) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: fallback
    });
  }
  const FormComponent = resolvedVersion === 2 ? VersionTwoForm : VersionOneForm;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!isReady && fallback, /*#__PURE__*/_jsx(Wrapper, {
      $visible: isReady,
      children: /*#__PURE__*/_jsx(FormComponent, {
        formId: formId,
        portalId: portalId,
        env: env,
        region: region,
        dispatch: dispatch
      })
    })]
  });
}